import type { Movie, NewsItem } from '@/types/content';

export function getMovieById(list: Movie[], id: number): Movie | undefined {
  return list.find((item) => item.id === id);
}

export function getNewsById(list: NewsItem[], id: number): NewsItem | undefined {
  return list.find((item) => item.id === id);
}

export function getHotMovies(list: Movie[], count: number): Movie[] {
  return [...list].sort((a, b) => b.heat - a.heat).slice(0, count);
}

export function getLatestMovies(list: Movie[], count: number): Movie[] {
  return [...list].sort((a, b) => b.year - a.year || b.heat - a.heat).slice(0, count);
}

export function getTopRatedMovies(list: Movie[], count: number): Movie[] {
  return [...list].sort((a, b) => b.rating - a.rating).slice(0, count);
}

export function getRelatedMovies(list: Movie[], current: Movie, count: number): Movie[] {
  return list
    .filter((item) => item.id !== current.id)
    .map((item) => {
      const genreHit = item.genres.filter((g) => current.genres.includes(g)).length;
      const typeHit = item.type === current.type ? 1 : 0;
      const regionHit = item.region === current.region ? 1 : 0;
      return { item, score: genreHit * 3 + typeHit * 2 + regionHit };
    })
    .sort((a, b) => b.score - a.score || b.item.heat - a.item.heat)
    .slice(0, count)
    .map((entry) => entry.item);
}

export interface MovieFilter {
  type?: string;
  genre?: string;
  year?: string | number;
  region?: string;
  sort?: string;
}

export function filterMovies(list: Movie[], filter: MovieFilter): Movie[] {
  let result = list.filter((item) => {
    if (filter.type && item.type !== filter.type) return false;
    if (filter.genre && !item.genres.includes(filter.genre)) return false;
    if (filter.year && String(item.year) !== String(filter.year)) return false;
    if (filter.region && item.region !== filter.region) return false;
    return true;
  });

  const sort = filter.sort || 'latest';
  if (sort === 'latest') {
    result = [...result].sort((a, b) => b.year - a.year || b.heat - a.heat);
  } else if (sort === 'heat') {
    result = [...result].sort((a, b) => b.heat - a.heat);
  } else if (sort === 'rating') {
    result = [...result].sort((a, b) => b.rating - a.rating);
  }

  return result;
}

export function searchMovies(list: Movie[], keyword: string): Movie[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return list.filter((item) => {
    const haystack = [item.title, item.director, ...item.actors, ...item.genres, item.region, String(item.year)]
      .join(' ')
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function searchNews(list: NewsItem[], keyword: string): NewsItem[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return list.filter((item) => {
    const haystack = [item.title, item.subtitle, item.category, item.summary, item.content.join(' ')]
      .join(' ')
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function paginate<T>(list: T[], page: number, pageSize: number): { items: T[]; totalPages: number } {
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return { items: list.slice(start, start + pageSize), totalPages };
}

export const sortOptions = [
  { value: 'latest', label: '按最新上映' },
  { value: 'heat', label: '按热门度' },
  { value: 'rating', label: '按评分' },
];